/**
 * PATH: apps/quote/src/pages/import-quote/useExistingQuoteImport.ts
 * FILE_ID: QUOTE-IMPORT-EXISTING-HOOK-001
 * PURPOSE: Load, expand, select, and copy existing Quotech quotes for import.
 */
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { quotesCrudApi } from '@/lib/api/quotes-crud-api'
import {
  dedupeByKey,
  fromJobEntry,
  fromListItem,
  groupFamilies,
  matchesSearch,
  selectNewFamilyMembers,
  selectOnMemberTick,
  toggleFamily,
} from './existingQuoteFamilies'
import { expandFamilyMembers, importExistingQuotes } from './importExistingQuotes'
import type { QuoteFamily } from './existingQuoteTypes'

export function useExistingQuoteImport(jobId?: string | null) {
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [extrasByFamily, setExtrasByFamily] = useState<Record<string, QuoteFamily['members']>>({})
  const [expanding, setExpanding] = useState<string | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  const listQuery = useQuery({
    queryKey: ['import-existing-quotes', debounced],
    queryFn: () => quotesCrudApi.listQuotes({ search: debounced || undefined }),
  })

  const jobQuery = useQuery({
    queryKey: ['import-existing-job-quotes', jobId],
    queryFn: () => quotesCrudApi.getJobQuotes(jobId as string),
    enabled: !!jobId,
  })

  const listed = useMemo(() => {
    const items = (listQuery.data || []).map(fromListItem)
    if (jobId) {
      for (const group of jobQuery.data?.groups || []) {
        for (const entry of group.entries || []) items.push(fromJobEntry(entry, jobId))
      }
    }
    return dedupeByKey(items)
  }, [listQuery.data, jobQuery.data, jobId])

  const families = useMemo(() => {
    const grouped = groupFamilies(listed, extrasByFamily)
    if (!debounced.trim()) return grouped
    return grouped
      .map((family) => ({
        ...family,
        members: family.members.filter((m) => matchesSearch(m, debounced)),
      }))
      .filter((family) => family.members.length > 0)
  }, [listed, extrasByFamily, debounced])

  const selectedMembers = useMemo(
    () => families.flatMap((f) => f.members).filter((m) => selected.has(m.key)),
    [families, selected],
  )

  const expandFamily = async (family: QuoteFamily) => {
    if (extrasByFamily[family.familyKey] || expanding) return
    const seed = family.members[0]
    if (!seed) return
    const previousKeys = family.members.map((m) => m.key)
    setExpanding(family.familyKey)
    try {
      const extras = await expandFamilyMembers(seed, quotesCrudApi)
      setExtrasByFamily((prev) => ({ ...prev, [family.familyKey]: extras }))
      const nextFamily = {
        ...family,
        members: dedupeByKey([...family.members, ...extras]),
      }
      setSelected((cur) => selectNewFamilyMembers(cur, previousKeys, nextFamily))
    } finally {
      setExpanding(null)
    }
  }

  const onToggleFamily = (family: QuoteFamily) => {
    setSelected((cur) => toggleFamily(cur, family))
    void expandFamily(family)
  }

  const onToggleMember = (family: QuoteFamily, key: string) => {
    setSelected((cur) => selectOnMemberTick(cur, family, key))
    void expandFamily(family)
  }

  const importMutation = useMutation({
    mutationFn: () => importExistingQuotes(selectedMembers, quotesCrudApi, jobId),
  })

  return {
    search,
    setSearch,
    families,
    selected,
    selectedMembers,
    expanding,
    listLoading: listQuery.isLoading || (!!jobId && jobQuery.isLoading),
    listError: listQuery.isError || jobQuery.isError,
    onToggleFamily,
    onToggleMember,
    importMutation,
  }
}
